import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { apiRequest } from "../../services/api";

const number = (value) => Number(value || 0);

export default function LowStockWidget() {
  const { accessToken } = useAuth();

  const [lowStock, setLowStock] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadLowStock = useCallback(async () => {
    if (!accessToken) return;

    try {
      setLoading(true);

      const response = await apiRequest("/inventory/reports/low-stock", {
        accessToken,
      });

      setLowStock(response || []);
    } catch {
      setLowStock([]);
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    loadLowStock();
  }, [loadLowStock]);

  const visibleItems = lowStock.slice(0, 3);

  const criticalCount = lowStock.filter((item) => {
    const minimum = number(item.minimumStock ?? item.reorderLevel);

    return number(item.availableQuantity) <= minimum;
  }).length;

  return (
    <div className="rounded-3xl border border-gray-100 bg-white p-6">
      <div className="mb-6 flex items-center justify-between">
        <h2 className="text-lg font-bold text-gray-900">
          Low Stock Alerts
        </h2>

        <span className="rounded-full bg-red-100 px-3 py-1 text-xs font-medium text-red-600">
          {criticalCount} Critical
        </span>
      </div>

      <div className="space-y-5">
        {loading ? (
          <p className="py-6 text-center text-sm text-gray-500">
            Loading alerts…
          </p>
        ) : visibleItems.length === 0 ? (
          <p className="py-6 text-center text-sm text-gray-500">
            No low-stock alerts. Great work.
          </p>
        ) : (
          visibleItems.map((item) => {
            const available = number(item.availableQuantity);
            const reorderLevel = number(item.reorderLevel);

            const percentage =
              reorderLevel > 0
                ? Math.max(0, Math.min(100, (available / reorderLevel) * 100))
                : 0;

            return (
              <div
                key={item.settingId}
                className="rounded-2xl border border-gray-100 p-4 hover:bg-gray-50"
              >
                <div className="mb-3 flex justify-between gap-3">
                  <div className="min-w-0">
                    <h4 className="truncate font-medium text-gray-900">
                      {item.variant?.product?.name ||
                        item.variant?.sku ||
                        "Product Variant"}
                    </h4>

                    <p className="mt-1 truncate text-xs text-gray-400">
                      {item.store?.name} · {item.variant?.sku}
                    </p>
                  </div>

                  <span className="font-bold text-red-600">
                    {available}
                  </span>
                </div>

                <div className="h-2 overflow-hidden rounded-full bg-gray-100">
                  <div
                    className="h-full rounded-full bg-red-500"
                    style={{ width: `${percentage}%` }}
                  />
                </div>

                <p className="mt-2 text-xs text-gray-400">
                  Reorder Level: {reorderLevel}
                </p>
              </div>
            );
          })
        )}
      </div>

      <button
        disabled
        title="Purchase Orders will be enabled after the Purchases module is built."
        className="mt-6 h-12 w-full cursor-not-allowed rounded-2xl bg-primary-700 text-sm font-semibold text-white opacity-60"
      >
        Create Purchase Order
      </button>
    </div>
  );
}